import classNames from "classnames";
import { SortableOptions, DataSourceSortDirection } from "client/Data/sorting";
import { FilterValueState, MultiSelectableOptions } from "client/Hooks";
import { Coordinate } from "client/Misc/commonTypes";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	forwardRef,
	MouseEvent,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	Ref,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { Anchor } from "../Anchor/Anchor";
import { DropOverMenu } from "../DropOverMenu/DropOverMenu";
import {
	ColumnFilterableFormBuilder,
	isColumnFilterableFormBuilder,
	SearchFilterableForm
} from "../FilterableForms/SearchFilterableForm";
import { Icon } from "../Icon/Icon";
import { Moveable } from "../Moveable/Moveable";
import { SelectAllCheckbox, SelectAllCheckboxValue } from "../SelectionCheckbox/SelectionCheckbox";
import styles from "./TableView.module.less";

export type RegularTableColumnWidth = number | "fill" | "auto";
export type VirtualTableColumnWidth = number | "fill";
export type TableColumnWidth = RegularTableColumnWidth | VirtualTableColumnWidth;

export type ColumnState = {
	id: string;
	width: TableColumnWidth;
	minWidth: number;
	maxWidth?: number;
};

export type ColumnProps = {
	index: number;
	columnState: ColumnState;
	onChangeColumnState(columnState: ColumnState, index: number): void;
	sortable: SortableOptions;
	filterable: FilterValueState<string> | ColumnFilterableFormBuilder | false;
	isResizable: boolean;
	trackingId: string;
	backgroundColor: string | undefined;
};

export const Column = (props: PropsWithChildren<ColumnProps>): ReactElement => {
	const {
		columnState,
		onChangeColumnState,
		sortable,
		filterable,
		isResizable,
		trackingId,
		index,
		backgroundColor,
		children
	} = props;

	const elementRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const [widthBeforeResize, setWidthBeforeResize] = useState<number>();

	const renderTitle = (): ReactElement => {
		return (
			<div
				{...setTrackingId("title")}
				className={styles.headerTitle}
			>
				{children}
			</div>
		);
	};

	const renderContent = (): ReactElement => {
		if (sortable) {
			return (
				<SortButton
					sortable={sortable}
				>
					{renderTitle()}
				</SortButton>
			);
		}

		return (
			<div className={styles.headerContent}>
				{renderTitle()}
			</div>
		);
	};

	const resizeStartHandler = useCallback(
		(): void => {
			const {
				current: element
			} = elementRef;

			if (!element) {
				console.error("Element is not set");
				return;
			}

			setWidthBeforeResize(element.clientWidth);
		},
		[]
	);

	const resizeHandler = useCallback(
		(offset: Coordinate): void => {
			if (isNil(widthBeforeResize)) {
				console.error("Width before resizing is not set.");
				return;
			}

			onChangeColumnState(
				{
					...columnState,
					width: widthBeforeResize + offset.x,
					maxWidth: undefined
				},
				index
			);
		},
		[
			columnState,
			index,
			onChangeColumnState,
			widthBeforeResize
		]
	);

	return (
		<ColumnHeader
			ref={elementRef}
			trackingId={trackingId}
			maxWidthOfAutoColumn={
				columnState.width === "auto"
					? columnState.maxWidth
					: undefined
			}
			backgroundColor={backgroundColor}
		>
			{renderContent()}
			{filterable && (
				<ColumnFilter filterable={filterable} />
			)}
			{isResizable && (
				<ResizeHandle
					onResizeStart={resizeStartHandler}
					onResize={resizeHandler}
				/>
			)}
		</ColumnHeader>
	);
};
Column.displayName = "Column";

export type SelectColumnProps = {
	multiSelectable: MultiSelectableOptions;
	backgroundColor: string | undefined;
};

export const SelectColumn = (props: SelectColumnProps): ReactElement => {
	const {
		multiSelectable,
		backgroundColor
	} = props;

	const selectHandler = useCallback(
		(shouldSelectAll: boolean): void => {
			if (shouldSelectAll) {
				multiSelectable.selectAll();
			} else {
				multiSelectable.selectNone();
			}
		},
		[multiSelectable]
	);

	const selectionValue: SelectAllCheckboxValue = multiSelectable.areAllSelected
		? true
		: multiSelectable.selectionCount > 0
			? "mixed"
			: false;

	return (
		<ColumnHeader
			backgroundColor={backgroundColor}
			trackingId="selection-column"
		>
			<SelectAllCheckbox
				value={selectionValue}
				onChange={selectHandler}
				trackingId="select-all"
			/>
		</ColumnHeader>
	);
};
SelectColumn.displayName = "SelectColumn";

export type ExtensionColumnProps = {
	backgroundColor: string | undefined;
};

export const ExtensionColumn = (props: PropsWithChildren<ExtensionColumnProps>): ReactElement => {
	const {
		backgroundColor,
		children
	} = props;

	return (
		<ColumnHeader
			trackingId="extensions"
			className={styles.headerColumn_extensionWrapper}
			backgroundColor={backgroundColor}
		>
			{children}
		</ColumnHeader>
	);
};
ExtensionColumn.displayName = "ExtensionColumn";

type ColumnHeaderProps = {
	trackingId: string;
	className?: string;
	maxWidthOfAutoColumn?: number;
	backgroundColor: string | undefined;
	children: ReactNode;
};

const ColumnHeader = forwardRef(
	(props: ColumnHeaderProps, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			trackingId,
			className,
			maxWidthOfAutoColumn,
			backgroundColor,
			children
		} = props;

		const stopPropagationToLassoContainer = useCallback(
			(event: MouseEvent): void => {
				event.stopPropagation();
			},
			[]
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				ref={ref}
				className={classNames(className, styles.headerColumn)}
				onMouseDown={stopPropagationToLassoContainer}
				style={{
					maxWidth: maxWidthOfAutoColumn,
					backgroundColor
				}}
			>
				{children}
			</div>
		);
	}
);
ColumnHeader.displayName = "ColumnHeader";

type SortButtonProps = {
	sortable: Exclude<SortableOptions, false>;
};

const SortButton = (props: PropsWithChildren<SortButtonProps>): ReactElement => {
	const {
		sortable,
		children
	} = props;

	const {
		direction,
		sort,
		disallowUnsorted = false
	} = sortable;

	const renderIcon = (): ReactElement | null => {
		switch (direction) {
			case DataSourceSortDirection.Ascending:
				return (
					<span className={styles.sortIcon}>
						<Icon value="chevron-up" />
					</span>
				);
			case DataSourceSortDirection.Descending:
				return (
					<span className={styles.sortIcon}>
						<Icon value="chevron-down" />
					</span>
				);
			case DataSourceSortDirection.None:
				return null;
			default:
				never(direction);
		}
	};

	const sortHandler = useCallback(
		(): void => {
			sort(getNextSortDirection(direction, disallowUnsorted));
		},
		[
			direction,
			disallowUnsorted,
			sort
		]
	);

	return (
		<Anchor
			onClick={sortHandler}
			className={styles.headerAnchor}
			trackingId={generateTrackingId(
				"sortable-title",
				directionToSubTrackingIdMap[direction]
			)}
		>
			{children}
			{renderIcon()}
		</Anchor>
	);
};
SortButton.displayName = "SortButton";

type ColumnFilterProps = {
	filterable: FilterValueState<string> | ColumnFilterableFormBuilder;
};

const ColumnFilter = (props: ColumnFilterProps): ReactElement => {
	const {
		filterable
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref: Ref<HTMLAnchorElement> = useRef<HTMLAnchorElement>(null);

	const clickHandler = useCallback(
		(): void => {
			setIsOpen(true);
		},
		[]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsOpen(false);
		},
		[]
	);

	const trackingId: string = "filter-handle";

	const hasFilter: boolean = isColumnFilterableFormBuilder(filterable)
		? filterable.isActive
		: isNotNilOrEmpty(filterable.value);

	return (
		<>
			<Anchor
				ref={ref}
				className={classNames(
					styles.headerAnchor,
					styles.headerFilterButton,
					{ [styles.headerFilterButton_active]: hasFilter }
				)}
				onClick={clickHandler}
				trackingId={trackingId}
			>
				<Icon value="filter" />
			</Anchor>
			{isOpen && isNotNil(ref.current) && (
				<ColumnFilterDropOver
					filterable={filterable}
					onClose={closeHandler}
					target={ref.current}
					trackingId={generateTrackingId(trackingId, "__dropover")}
				/>
			)}
		</>
	);
};
ColumnFilter.displayName = "ColumnFilter";

export type ColumnFilterDropOverProps = {
	target: HTMLElement;
	filterable: FilterValueState<string> | ColumnFilterableFormBuilder;
	onClose(): void;
	trackingId: string;
};

export const ColumnFilterDropOver = (props: ColumnFilterDropOverProps): ReactElement => {
	const {
		target,
		filterable,
		onClose,
		trackingId
	} = props;

	return (
		<DropOverMenu
			target={target}
			onClose={onClose}
			title={localize("_Filter")}
			trackingId={trackingId}
		>
			<div className={styles.headerFilterDropOver}>
				{isColumnFilterableFormBuilder(filterable)
					? filterable.columnFilterableFormFactory(onClose)
					: (
						<SearchFilterableForm
							filterable={filterable}
							onClose={onClose}
						/>
					)
				}
			</div>
		</DropOverMenu>
	);
};
ColumnFilterDropOver.displayName = "ColumnFilterDropOver";

type ResizeHandleProps = {
	onResizeStart(): void;
	onResize(offset: Coordinate): void;
};

const ResizeHandle = (props: ResizeHandleProps): ReactElement => {
	const {
		onResizeStart,
		onResize
	} = props;

	return (
		<div className={styles.resizeHandle}>
			<Moveable
				onStart={onResizeStart}
				onMove={onResize}
			/>
		</div>
	);
};
ResizeHandle.displayName = "ResizeHandle";

const directionToSubTrackingIdMap: {[key in DataSourceSortDirection]: string} = {
	[DataSourceSortDirection.Ascending]: "|up",
	[DataSourceSortDirection.Descending]: "|down",
	[DataSourceSortDirection.None]: "|none"
};

function getNextSortDirection(
	sortDirection: DataSourceSortDirection,
	disallowUnsorted: boolean
): DataSourceSortDirection {
	switch (sortDirection) {
		case DataSourceSortDirection.Ascending:
			return DataSourceSortDirection.Descending;
		case DataSourceSortDirection.Descending:
			return disallowUnsorted
				? DataSourceSortDirection.Ascending
				: DataSourceSortDirection.None;
		case DataSourceSortDirection.None:
			return DataSourceSortDirection.Ascending;
		default:
			never(sortDirection);
	}
}